"use client";

import type { DeltaNeutralBotDto } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

const ACTIVE_DN_STATUSES = ["PENDING", "OPENING", "RUNNING", "CLOSING"] as const;
const DN_POLLING_INTERVAL_MS = 30_000;

export function useDnBots(enabled = true) {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();

  const { data, isLoading, isError, error, refetch } = useQuery<{ bots: DeltaNeutralBotDto[] }>({
    queryKey: [QueryKeys.DELTA_NEUTRAL_BOTS, hubUser?.id],
    queryFn: () =>
      apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_DELTA_NEUTRAL_BOTS, {}) as Promise<{
        bots: DeltaNeutralBotDto[];
      }>,
    enabled: enabled && !!hubUser?.id,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const bots = query.state.data?.bots;
      const hasActive = bots?.some((b) => (ACTIVE_DN_STATUSES as readonly string[]).includes(b.status));
      return hasActive ? DN_POLLING_INTERVAL_MS : false;
    },
  });

  return {
    dnBots: data?.bots ?? [],
    isLoading: enabled && (isLoading || !hubUser?.id),
    isError,
    error,
    refetch,
  };
}
